/**
 * What this person still owes — §7.7 from the reader's side.
 *
 * Three filters run inside the query behind this list, and each of them is the
 * reason a row is here or is not:
 *
 * - **Current cycles only.** A superseded cycle cannot be signed, so listing one
 *   would be an obligation nobody can discharge.
 * - **In the audience right now.** This is auto-enrolment seen from the person's
 *   side: a new hire's list is populated by requests opened before they were
 *   provisioned, with nothing having enrolled them.
 * - **Through `permission_visible_nodes`.** A role audience can reach somebody
 *   with no grant on the document, and a nag naming a document the reader cannot
 *   open is worse than no nag. The gap is a grant problem and should surface as
 *   one rather than as an unopenable item on a to-do list.
 *
 * **There is no one-click acknowledge here, on purpose.** The row this writes
 * means "I read this version", and a button on a list of titles is an invitation
 * to say that about a document nobody opened. The action lives where the
 * document is — on the document screen, and on the cycle page next to the text
 * that says which version is being attested to.
 */

import { Link } from '@tanstack/react-router';
import { useAcknowledgmentHistory } from '../../api/queries.ts';
import { formatRelative } from '../../components/format.ts';
import { Badge } from '../../components/ui/badge.tsx';
import { EmptyState, Loading, PageError } from '../../components/ui/feedback.tsx';
import { Card, PageHeader } from '../../components/ui/layout.tsx';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';
import { type CycleClosure, cycleClosure, DueDate } from './vocabulary.tsx';

/** Nothing at all while the signature is still against the live text. */
function closureLabel(closure: CycleClosure) {
  if (!closure) return null;
  return <Badge tone="gray">{closure === 'archived' ? 'Archived' : 'Superseded'}</Badge>;
}

function headline(total: number, overdue: number): string {
  if (total === 0) return 'You are up to date';
  const noun = total === 1 ? 'document is' : 'documents are';
  if (overdue === 0) return `${total} ${noun} waiting on your acknowledgment`;
  return `${total} ${noun} waiting on you — ${overdue} past the date`;
}

export function AcknowledgmentsPage() {
  const query = useAcknowledgmentHistory();

  if (query.isError) {
    return (
      <PageError error={query.error} title="What you owe" back={{ href: '/', label: 'Overview' }} />
    );
  }

  const owed = query.data?.outstanding ?? [];
  const signed = query.data?.signed ?? [];

  return (
    <>
      <PageHeader
        title={query.isPending ? 'What you owe' : headline(owed.length, query.data?.overdue ?? 0)}
        description={
          <>
            Each of these asks you to confirm you have read one specific version. Acknowledging says
            nothing about the next revision — publishing one opens a fresh cycle, which is what
            keeps &quot;everyone acknowledged the expense policy&quot; from staying true after a
            rewrite (§7.7).
          </>
        }
        actions={
          // The cross-document view is `audit.view` and answers 403 for everyone
          // else; the link is left visible because a refusal that explains itself
          // beats a page whose existence is a secret.
          <Link to="/acknowledgments/compliance" className="text-sm link">
            Compliance across every document
          </Link>
        }
      />

      <Card padded={false}>
        {query.isPending ? (
          <Loading />
        ) : owed.length === 0 ? (
          <EmptyState
            title="Nothing is waiting on you"
            description="Either nobody has opened a cycle whose audience you resolve into, or you have signed every one that is current. A cycle you already acknowledged drops off this list; it does not sit here ticked."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Document</TH>
                <TH>Version</TH>
                <TH>Due</TH>
                <TH>Asked</TH>
                <TH>{''}</TH>
              </TR>
            </THead>
            <TBody>
              {owed.map((item) => (
                <TR key={item.requestId}>
                  <TD>
                    <span className="flex flex-wrap items-center gap-2">
                      <Link
                        to="/documents/$nodeId"
                        params={{ nodeId: item.documentId }}
                        className="font-medium text-primary no-underline hover:underline"
                      >
                        {item.title}
                      </Link>
                      {item.overdue ? <Badge tone="warning">Overdue</Badge> : null}
                    </span>
                  </TD>
                  <TD className="text-xs text-tertiary">v{item.versionNumber}</TD>
                  <TD>
                    <DueDate dueDate={item.dueDate} />
                  </TD>
                  <TD className="text-xs text-tertiary">{formatRelative(item.createdAt)}</TD>
                  <TD>
                    <Link
                      to="/acknowledgments/$requestId"
                      params={{ requestId: item.requestId }}
                      className="text-sm link"
                    >
                      The cycle
                    </Link>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/*
        §7.7 gives the owner an N-of-M view and admins a cross-document one, and
        gave the person in the audience nothing. "Did I sign this, and which
        version was it" is the question they actually have, and answering it
        anywhere else means asking somebody who can see the roster.

        A signature against a superseded version stays here rather than being
        filtered out. It is a true record of what was read — which is why cycles
        are scoped to versions at all — and dropping it would make this page
        claim they never read the old policy. When the revision is waiting, it
        appears in the list above, and the two read as the pair they are.
      */}
      <PageHeader title="What you have acknowledged" />
      <Card padded={false}>
        {query.isPending ? (
          <Loading />
        ) : signed.length === 0 ? (
          <EmptyState
            title="Nothing yet"
            description="Acknowledgments you give will be listed here, with the version each one was against."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Document</TH>
                <TH>Version</TH>
                <TH>Acknowledged</TH>
              </TR>
            </THead>
            <TBody>
              {signed.map((item) => (
                <TR key={item.requestId}>
                  <TD>
                    <span className="flex flex-wrap items-center gap-2">
                      <Link
                        to="/documents/$nodeId"
                        params={{ nodeId: item.documentId }}
                        className="font-medium text-primary no-underline hover:underline"
                      >
                        {item.title}
                      </Link>
                      {/*
                        Two ways a signature stops being against the live text,
                        and they say different things to the person: a newer
                        version is waiting, or the document is gone. Named
                        through `cycleClosure` so this table and the cycle badge
                        cannot draw the distinction differently.
                      */}
                      {closureLabel(cycleClosure(item))}
                    </span>
                  </TD>
                  <TD className="text-xs text-tertiary">v{item.versionNumber}</TD>
                  <TD className="text-xs text-tertiary">{formatRelative(item.acknowledgedAt)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </>
  );
}
